import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';
import { FaCity, FaLock, FaLayerGroup, FaTimes, FaSave } from 'react-icons/fa';
import Select from 'react-select';
import { districtAPI, talukaAPI } from '../../../../utils/api';
import '../../../../styles/institutional-form.css';
import '../css/districtCreate.css';

function DistrictCreate({ district = null, onClose, onSubmit }) {
  const isEdit = !!district;

  const [code, setCode]       = useState('');
  const [name, setName]       = useState('');
  const [talukaIds, setTalukaIds] = useState([]);
  const [talukaOpts, setTalukaOpts] = useState([]);
  const [errors, setErrors]   = useState({});
  const [busy, setBusy]       = useState(false);
  const [codeLoading, setCodeLoading] = useState(!isEdit);

  useEffect(() => {
    talukaAPI.getAll()
      .then(r => setTalukaOpts((r.data || []).map(t => ({ value: Number(t.id), label: t.name }))))
      .catch(() => setTalukaOpts([]));

    if (isEdit) {
      setCode(district.code || district.district_id || '');
      setName(district.name || '');
      const ids = Array.isArray(district.taluka_ids)
        ? district.taluka_ids.map(Number)
        : String(district.taluka_ids || '').split('|').filter(Boolean).map(Number);
      setTalukaIds(ids);
    } else {
      setCodeLoading(true);
      districtAPI.nextCode()
        .then(r => setCode(r.data.code))
        .catch(() => setCode('DST-???'))
        .finally(() => setCodeLoading(false));
    }
  }, [isEdit, district]);

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = 'District name is required';
    if (name.trim().length > 1 && !/^[a-zA-Z\s\-.]+$/.test(name.trim())) e.name = 'Only letters, spaces, hyphens, and periods';
    if (!talukaIds.length) e.talukaIds = 'Select at least one taluka';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async ev => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = { name: name.trim(), code, taluka_ids: talukaIds };
      if (isEdit) await districtAPI.update(district.id, payload);
      else        await districtAPI.create(payload);
      Swal.fire('Success', isEdit ? 'District updated.' : 'District created.', 'success');
      onSubmit?.();
      onClose?.();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.error || err.message || 'Failed', 'error');
    } finally { setBusy(false); }
  };

  return (
    <form className="ifrm" onSubmit={handleSubmit}>

      <div className="ifrm-section">
        <div className="ifrm-section-title"><FaLock style={{color:'#10ac84'}} /> System Identifier</div>
        <div className="ifrm-field">
          <label className="ifrm-label">
            District Code <span className="ifrm-badge-auto">Auto-generated</span>
          </label>
          {codeLoading
            ? <p className="ifrm-code-loading">Generating code…</p>
            : (
              <div className="ifrm-locked-wrap">
                <FaLock className="ifrm-lock-icon" />
                <input className="ifrm-input" value={code} disabled />
              </div>
            )
          }
        </div>
      </div>

      <div className="ifrm-section">
        <div className="ifrm-section-title"><FaCity style={{color:'#0f6fad'}} /> District Details</div>

        <div className="ifrm-field">
          <label className="ifrm-label">
            District Name <span className="ifrm-required">*</span>
          </label>
          <input
            className={`ifrm-input${errors.name ? ' error' : ''}`}
            placeholder="e.g. Hyderabad"
            value={name}
            onChange={e => { setName(e.target.value); setErrors(p => ({...p, name:''})); }}
            disabled={busy}
          />
          {errors.name && <p className="ifrm-error">{errors.name}</p>}
        </div>

        <div className="ifrm-field">
          <label className="ifrm-label">
            <FaLayerGroup style={{color:'#64748b'}} /> Linked Talukas <span className="ifrm-required">*</span>
          </label>
          <Select
            isMulti
            classNamePrefix="ifrm-rselect"
            className="ifrm-rselect"
            options={talukaOpts}
            value={talukaOpts.filter(o => talukaIds.includes(o.value))}
            onChange={opts => { setTalukaIds(opts.map(o => o.value)); setErrors(p => ({...p, talukaIds:''})); }}
            placeholder="Select talukas…"
            isDisabled={busy}
          />
          {errors.talukaIds && <p className="ifrm-error">{errors.talukaIds}</p>}
        </div>
      </div>

      <div className="ifrm-actions">
        <button type="button" className="ifrm-btn ifrm-btn-cancel" onClick={onClose} disabled={busy}>
          <FaTimes /> Cancel
        </button>
        <button type="submit" className="ifrm-btn ifrm-btn-submit" disabled={busy || codeLoading}>
          {busy ? <><span className="ifrm-spinner" /> Saving…</> : <><FaSave /> {isEdit ? 'Update District' : 'Create District'}</>}
        </button>
      </div>
    </form>
  );
}

DistrictCreate.propTypes = { district: PropTypes.object, onClose: PropTypes.func, onSubmit: PropTypes.func };
export default DistrictCreate;
